import { useInjection } from 'inversify-react';
import { useCallback, useEffect, useState } from 'react';

import { TranslationProvider as Translation } from '@desygner/ui-common-translation';
import { IMonitoringService } from '@desygner/ui-core-monitoring';

import { TYPES } from '../inversify.config';
import { LayoutSkeleton } from '../layout/components/Skeleton/LayoutSkeleton';
import { IConfigStoreInjector, IViewerStoreInjector } from '../store';
import { fetchTranslations } from '../utils/locale.utils';

export type TranslationProviderProps = {
  children: React.ReactNode;
};

export const TranslationProvider = ({ children }: TranslationProviderProps) => {
  const useConfigStore = useInjection<IConfigStoreInjector>(TYPES.Store.Config);
  const useViewerStore = useInjection<IViewerStoreInjector>(TYPES.Store.Viewer);
  const monitoringService = useInjection<IMonitoringService>(TYPES.MonitoringService);
  const language = useConfigStore((state) => state.language);
  const translationUrls = useConfigStore((state) => state.translationUrls);
  const isTranslationLoaded = useViewerStore((state) => state.isTranslationLoaded);
  const setIsTranslationLoaded = useViewerStore((state) => state.setIsTranslationLoaded);
  const [translation, setLanguage] = useState({
    language,
    translationContent: {},
  });
  const onTranslationMissing = useCallback(
    ({ translationKey }: { language: string[]; translationKey: string }) => {
      monitoringService.logException(new Error(`Missing translation key: ${translationKey}`));
    },
    [monitoringService]
  );

  useEffect(
    function loadingTranslations() {
      fetchTranslations(translationUrls).then((content) => {
        setLanguage({
          language: translation.language,
          translationContent: content,
        });

        setIsTranslationLoaded(true);
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  if (!isTranslationLoaded) {
    return <LayoutSkeleton />;
  }

  return (
    <Translation {...translation} onMissingKey={onTranslationMissing}>
      {children}
    </Translation>
  );
};
